import { RangeSensitivity } from 'api/models/RangeSensitivity';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  RANGE_SENSITIVITY_SPREADSHEET_LAST_VALID_PCT,
  RANGE_SENSITIVITY_SPREADSHEET_SENSITIVITY_PCT,
} from 'pages/ValuationsAllocation/common/constants/rangeSensitivity/sheetAliases';
import { getNumberValue } from 'utilities';
import afterCellChangedPct from './afterCellChangedPct';
import { customParserPct } from './customParserPct';
import { customReverseParserPct } from './customReverseParserPct';
import { customValidationsPct } from './customValidationsPct';
import { COLUMN_HEADER_SENSITIVITY_RANGE } from '../constants';

export interface CreateSensitivityPctConfigurationParams {
  rangeSensitivity: RangeSensitivity;
  pctWorkbookKey: string;
  panelId: string;
  isDisabled: boolean;
}

type PctSpreadsheetConfig = SpreadsheetConfig<object, object, object>;

const createSensitivityPctConfiguration = (
  params: CreateSensitivityPctConfigurationParams
): PctSpreadsheetConfig => {
  const { rangeSensitivity, pctWorkbookKey, panelId, isDisabled } = params;
  const pct = getNumberValue(rangeSensitivity?.sensitivity_percentage);
  const pctDisplay = pct * 100;

  const rowConfig = [
    {
      alias: RANGE_SENSITIVITY_SPREADSHEET_SENSITIVITY_PCT,
      gridType: 'string',
      ignoreRowCopy: true,
      value: COLUMN_HEADER_SENSITIVITY_RANGE,
    },
    {
      alias: RANGE_SENSITIVITY_SPREADSHEET_LAST_VALID_PCT,
      gridType: 'number',
      ignoreRowCopy: true,
      isVisible: false,
      value: '',
    },
  ];

  // Single column: row 1 = editable %, row 2 = hidden last-valid value for cross-sheet formulas
  const columns = [
    {
      [RANGE_SENSITIVITY_SPREADSHEET_SENSITIVITY_PCT]: { value: pctDisplay },
      [RANGE_SENSITIVITY_SPREADSHEET_LAST_VALID_PCT]: { value: pctDisplay },
    },
  ];

  const parser: PctSpreadsheetConfig['parser'] = parserParams => customParserPct(parserParams);

  const reverseParser: NonNullable<PctSpreadsheetConfig['reverseParser']> = reverseParserParams => {
    customReverseParserPct({
      cells: reverseParserParams.cells,
      rangeSensitivity,
    });
  };

  return new SpreadsheetConfig({
    name: pctWorkbookKey,
    rowConfig,
    columns,
    parser,
    reverseParser,
    customValidations: customValidationsPct as unknown as PctSpreadsheetConfig['customValidations'],
    afterCellChanged: afterCellChangedPct,
    showTitlesColumn: true,
    hasColTitle: false,
    currencyFormatter: false,
    unitsFormatter: false,
    tableData: { isDisabled, panelId },
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig<object, object, object>>[0]) as PctSpreadsheetConfig;
};

export default createSensitivityPctConfiguration;
